/**
 * Technical Development Score Calculator
 * 
 * Calculates percentage distribution and 0-10 scores for each skill area
 * based on the athlete's skill evaluations.
 */

// Skill categories with their respective skills
export const SKILL_CATEGORIES = {
  TECHNICAL: {
    name: 'Technical',
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-500/20',
    borderColor: 'border-emerald-500/30',
    skills: ['ball_control', 'passing', 'dribbling', 'first_touch', 'finishing', 'shooting']
  },
  PHYSICAL: {
    name: 'Physical',
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/20',
    borderColor: 'border-blue-500/30',
    skills: ['speed', 'acceleration', 'agility', 'coordination', 'balance', 'explosiveness', 'endurance']
  },
  TACTICAL: {
    name: 'Tactical / IQ',
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-500/20',
    borderColor: 'border-yellow-500/30',
    skills: ['positioning', 'decision_making', 'vision', 'game_awareness', 'reaction_time']
  }
};

// Map skill status to default numeric value (0-10 scale)
// Meybra V3 Scoring (Conservative):
// - Focus: 4.0-6.0, default 5.0
// - Developing: 6.1-8.0, default 7.0
// - Proficient: 8.1-10.0, default 8.3 (10 only for exceptional)
const STATUS_TO_SCORE = {
  'Focus': 5.0,
  'Developing': 7.0,
  'Proficient': 8.3
};

/**
 * Get numeric score from skill data
 * Uses manual score if available, otherwise falls back to status-based default
 * @param {object} skillData - Skill data object with status and optional score
 * @returns {number} - Score value (0 if not set)
 */
export const getSkillScore = (skillData) => {
  if (!skillData?.status) return 0;
  
  // If manual score is provided and valid, use it
  if (skillData.score !== undefined && skillData.score !== null && !isNaN(skillData.score)) {
    return parseFloat(skillData.score);
  }
  
  // Otherwise, use status-based default
  return STATUS_TO_SCORE[skillData.status] || 0;
};

/**
 * Get default score from status string (for backwards compatibility)
 * @param {string} status - 'Focus', 'Developing', or 'Proficient'
 * @returns {number} - Default score for the status
 */
export const getStatusScore = (status) => {
  return STATUS_TO_SCORE[status] || 0;
};

/**
 * Calculate average score for a skill area
 * Uses manual scores when available, otherwise uses status-based defaults
 * @param {object} techDev - Technical development data object
 * @param {string[]} skills - Array of skill keys for the area
 * @returns {number} - Average score (0-10)
 */
export const calculateAreaAverage = (techDev, skills) => {
  if (!techDev || skills.length === 0) return 0;
  
  let totalScore = 0;
  let evaluatedCount = 0;
  
  skills.forEach(skillKey => {
    const skillData = techDev[skillKey];
    if (skillData?.status) {
      const score = getSkillScore(skillData);
      if (score > 0) {
        totalScore += score;
        evaluatedCount++;
      }
    }
  });
  
  // Return average only if at least one skill is evaluated
  return evaluatedCount > 0 ? totalScore / evaluatedCount : 0;
};

/**
 * Round score to 1 decimal place
 * @param {number} score - Score value
 * @returns {number} - Rounded score
 */
export const roundScore = (score) => {
  return Math.round(score * 10) / 10;
};

/**
 * Calculate all development profile metrics
 * @param {object} techDev - Technical development data object
 * @returns {object} - Complete profile with scores and percentages
 */
export const calculateDevelopmentProfile = (techDev) => {
  // Calculate averages for each area (now directly in 0-10 scale)
  const technicalAvg = calculateAreaAverage(techDev, SKILL_CATEGORIES.TECHNICAL.skills);
  const physicalAvg = calculateAreaAverage(techDev, SKILL_CATEGORIES.PHYSICAL.skills);
  const tacticalAvg = calculateAreaAverage(techDev, SKILL_CATEGORIES.TACTICAL.skills);
  
  // Calculate total for percentage distribution
  const totalAvgSum = technicalAvg + physicalAvg + tacticalAvg;
  
  // Calculate percentages (handle case where no skills are evaluated)
  let technicalPct = 0;
  let physicalPct = 0;
  let tacticalPct = 0;
  
  if (totalAvgSum > 0) {
    technicalPct = Math.round((technicalAvg / totalAvgSum) * 100);
    physicalPct = Math.round((physicalAvg / totalAvgSum) * 100);
    tacticalPct = Math.round((tacticalAvg / totalAvgSum) * 100);
    
    // Adjust for rounding to ensure total = 100
    const total = technicalPct + physicalPct + tacticalPct;
    if (total !== 100) {
      // Add/subtract difference from the largest percentage
      const diff = 100 - total;
      const maxPct = Math.max(technicalPct, physicalPct, tacticalPct);
      if (technicalPct === maxPct) technicalPct += diff;
      else if (physicalPct === maxPct) physicalPct += diff;
      else tacticalPct += diff;
    }
  }
  
  // Scores are now directly the averages (already in 0-10 scale)
  const technicalScore = roundScore(technicalAvg);
  const physicalScore = roundScore(physicalAvg);
  const tacticalScore = roundScore(tacticalAvg);
  
  // Calculate overall score (average of all three)
  const overallScore = totalAvgSum > 0 
    ? roundScore((technicalScore + physicalScore + tacticalScore) / 3)
    : 0;
  
  // Count evaluated skills
  const allSkills = [
    ...SKILL_CATEGORIES.TECHNICAL.skills,
    ...SKILL_CATEGORIES.PHYSICAL.skills,
    ...SKILL_CATEGORIES.TACTICAL.skills
  ];
  const evaluatedSkills = allSkills.filter(key => techDev?.[key]?.status).length;
  
  return {
    technical: {
      average: roundScore(technicalAvg),
      score: technicalScore,
      percentage: technicalPct,
      evaluatedCount: SKILL_CATEGORIES.TECHNICAL.skills.filter(k => techDev?.[k]?.status).length,
      totalCount: SKILL_CATEGORIES.TECHNICAL.skills.length
    },
    physical: {
      average: roundScore(physicalAvg),
      score: physicalScore,
      percentage: physicalPct,
      evaluatedCount: SKILL_CATEGORIES.PHYSICAL.skills.filter(k => techDev?.[k]?.status).length,
      totalCount: SKILL_CATEGORIES.PHYSICAL.skills.length
    },
    tactical: {
      average: roundScore(tacticalAvg),
      score: tacticalScore,
      percentage: tacticalPct,
      evaluatedCount: SKILL_CATEGORIES.TACTICAL.skills.filter(k => techDev?.[k]?.status).length,
      totalCount: SKILL_CATEGORIES.TACTICAL.skills.length
    },
    overall: {
      score: overallScore,
      evaluatedSkills,
      totalSkills: allSkills.length
    },
    // Raw averages for future use (radar charts, etc.)
    rawAverages: {
      technical: technicalAvg,
      physical: physicalAvg,
      tactical: tacticalAvg
    }
  };
};

/**
 * Determine player archetype based on profile
 * Score scale: 4.0 (Focus) to 10 (Proficient exceptional)
 * @param {object} profile - Development profile from calculateDevelopmentProfile
 * @returns {object} - Archetype info { name, description, icon }
 */
export const determineArchetype = (profile) => {
  const { technical, physical, tactical } = profile;
  
  // Need at least some evaluation to determine archetype
  if (profile.overall.evaluatedSkills < 5) {
    return { name: 'Undefined', description: 'More evaluations needed', icon: '❓' };
  }
  
  const maxScore = Math.max(technical.score, physical.score, tactical.score);
  const minScore = Math.min(technical.score, physical.score, tactical.score);
  const range = maxScore - minScore;
  
  // Balanced player (all areas within 1 point on the 5-10 scale)
  if (range <= 1) {
    return { name: 'Complete Player', description: 'Well-rounded across all areas', icon: '⭐' };
  }
  
  // Dominant area (1.5+ points higher than others)
  if (technical.score === maxScore && technical.score > physical.score + 1 && technical.score > tactical.score + 1) {
    return { name: 'Technical Creator', description: 'Exceptional ball skills and technique', icon: '⚽' };
  }
  
  if (physical.score === maxScore && physical.score > technical.score + 1 && physical.score > tactical.score + 1) {
    return { name: 'Athletic Force', description: 'Superior physical attributes', icon: '💪' };
  }
  
  if (tactical.score === maxScore && tactical.score > technical.score + 1 && tactical.score > physical.score + 1) {
    return { name: 'Smart Playmaker', description: 'High game intelligence and vision', icon: '🧠' };
  }
  
  // Two-area combinations
  if (technical.score >= physical.score && technical.score >= tactical.score - 0.5) {
    if (physical.score >= tactical.score) {
      return { name: 'Skillful Athlete', description: 'Technical ability with physical prowess', icon: '🏃' };
    } else {
      return { name: 'Creative Mind', description: 'Technical skills with tactical awareness', icon: '🎯' };
    }
  }
  
  if (physical.score >= tactical.score) {
    return { name: 'Dynamic Player', description: 'Physical with tactical understanding', icon: '⚡' };
  }
  
  return { name: 'Developing Talent', description: 'Growing across multiple areas', icon: '📈' };
};
